import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MoreVertical, Loader2, AlertTriangle, ChevronDown, StickyNote, Mail, PhoneCall,
  Users2, CalendarClock, HeartPulse, Link2 as LinkIcon, Check, FileText, Pencil,
  UserCog, Ban, Trash2, Sparkles, Plus, RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { ProspectHubspotSyncDialog } from "../components/dashboard/prospects/ProspectHubspotSyncDialog";
import RequestApprovalDialog from "../components/dashboard/RequestApprovalDialog";
import { formatDate, formatDateTime } from "../lib/date";
import {
  ACTIVITY_TYPES, STATUS_ORDER, STATUS_META, STATUS_TONE, STAGE_STEPS,
  CALL_OUTCOME_ORDER, CALL_OUTCOME_LABELS, NEXT_ACTION_LABELS, SignupBadge,
} from "../components/dashboard/prospects/prospectConstants";
import QuotesPanel from "../components/dashboard/quotes/QuotesPanel";
import ConvertToClientDialog from "../components/dashboard/prospects/ConvertToClientDialog";
import { ContactsEditor } from "../components/dashboard/ContactsEditor";
import { useSubStatuses, subTypeFor } from "../components/dashboard/prospects/subStatus";
import { CURRENCIES, formatMoney } from "../lib/money";
import { OverseasBadge } from "../components/dashboard/OverseasBadge";
import { EntityTodosCard } from "../components/dashboard/todos/EntityTodosCard";
import { EmailBody, Attachments, DirectionBadge } from "../components/dashboard/CapturedEmail";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v, cur = "AUD") => formatMoney(v, cur);
// Timeline row stamp: show date + time when a real timestamp exists, else date only.
const rowStamp = (it) => (it && it.at) ? formatDateTime(it.at) : (it && it.date ? formatDate(it.date) : "");

// Milestone tracks derived from the real status model (no fabricated stages).
const M1 = [
  { s: "not_contacted", label: "Not contacted" },
  { s: "emailed", label: "Emailed" },
  { s: "called", label: "Called" },
  { s: "responded", label: "Responded" },
  { s: "demo_requested", label: "Demo requested" },
];
const M2 = [
  { s: "meeting_booked", label: "Meeting booked" },
  { s: "demo_completed", label: "Demo completed" },
  { s: "quote_sent", label: "Quote sent" },
  { s: "trialing", label: "Trialing" },
  { s: "converted", label: "Converted" },
];
const KIND_GLYPH = {
  note: StickyNote, email: Mail, call: PhoneCall, meeting: Users2,
  task: CalendarClock, followup: CalendarClock, health: HeartPulse, linkedin: LinkIcon, sms: Mail, inbound: Mail,
};
const KIND_LABEL = {
  note: "NOTE", email: "EMAIL", call: "CALL", meeting: "MEETING", task: "TASK",
  followup: "FOLLOW-UP", health: "HEALTH", linkedin: "LINKEDIN", sms: "SMS", inbound: "INBOUND",
};
const FILTERS = [
  { key: "all", label: "All" }, { key: "note", label: "Notes" }, { key: "email", label: "Emails" },
  { key: "call", label: "Calls" }, { key: "task", label: "Tasks" }, { key: "meeting", label: "Meetings" },
];

function displayId(rec) {
  const d = formatDate(rec.created_at)?.split("-").reverse().join("") || "00000000";
  const tail = (rec.id || "").replace(/-/g, "").slice(-4).toUpperCase() || "0000";
  return `PR-${d}-${tail}`;
}
function daysAgo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10)); const t = new Date();
  d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0);
  const diff = Math.floor((t - d) / 86400000);
  if (diff <= 0) return "today";
  return diff === 1 ? "1 day ago" : `${diff} days ago`;
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10)); const t = new Date();
  d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

function HeaderStatusPill({ status }) {
  const t = STATUS_TONE[status];
  if (!t || t.quiet) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="rounded-full" style={{ width: 6, height: 6, background: "#8A857A" }} />
        <span className="text-[12.5px] text-[#59554D]">{t?.label || (status || "").replace(/_/g, " ")}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="pv-status-pill"
      style={{ background: t.fill, color: t.ink, border: `1px solid ${t.border}`, borderRadius: 999, padding: "3px 10px" }}>
      <span className="rounded-full" style={{ width: 5, height: 5, background: t.dot }} />
      <span className="text-[10.5px] font-bold tracking-[0.04em]">{t.label}</span>
    </span>
  );
}

function StageTrack({ title, steps, currentIndex, activeTrack, dateFor }) {
  const doneCount = activeTrack ? currentIndex + 1 : (currentIndex === -1 ? 0 : steps.length);
  const complete = doneCount >= steps.length;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[12.5px] font-semibold text-[#1A1A1A]">{title}</span>
        <span className="mono text-[11px] text-[#59554D]">{doneCount}/{steps.length}</span>
        {complete && (
          <span className="inline-flex items-center gap-1 ml-auto" style={{ background: "#E3F3E9", color: "#0B4F2E", border: "1px solid #A9D9BE", borderRadius: 999, padding: "1px 8px" }}>
            <span className="rounded-full" style={{ width: 4, height: 4, background: "#0E6B3F" }} />
            <span className="text-[10px] font-bold">Complete</span>
          </span>
        )}
      </div>
      <div className="space-y-2">
        {steps.map((st, i) => {
          const done = i < doneCount;
          const isNext = activeTrack && i === doneCount;
          const dt = dateFor(st.s);
          return (
            <div key={st.s} className="flex items-center gap-2.5">
              <span className="shrink-0 flex items-center justify-center rounded-full"
                style={{ width: 15, height: 15, background: done ? "#0E6B3F" : "#FFFFFF", border: done ? "none" : "1.5px solid #C9C3B6" }}>
                {done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </span>
              <span className={`text-[12.5px] flex-1 ${isNext ? "font-semibold text-[#1A1A1A]" : done ? "text-[#59554D]" : "text-[#8A857A]"}`}>{st.label}</span>
              <span className="mono text-[11px] text-[#59554D]">{dt ? formatDate(dt) : <span className="text-[#B5AFA3]">—</span>}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityBody({ text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 120 || text.includes("\n");
  return (
    <div className="mt-1">
      <div className="text-[13px] text-[#59554D] leading-[1.6] whitespace-pre-wrap break-words"
        style={!open && long ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}>
        {text}
      </div>
      {long && (
        <button onClick={() => setOpen((v) => !v)} className="text-[12px] font-semibold text-[#8A6D00] hover:underline mt-0.5" data-testid="pv-activity-toggle">
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

export default function ProspectRecordView({ recordId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [makingTodo, setMakingTodo] = useState(null);
  const [todoKey, setTodoKey] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("all");
  const [shown, setShown] = useState(6);
  const [convertOpen, setConvertOpen] = useState(false);

  // enquiry summary
  const [enq, setEnq] = useState(null);
  const [enqLoading, setEnqLoading] = useState(false);
  const [enqOpen, setEnqOpen] = useState(false);

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hubspotOpen, setHubspotOpen] = useState(false);
  const [delItem, setDelItem] = useState(null);

  // touch dialog
  const emptyTouch = { type: "note", activity_date: new Date().toISOString().slice(0, 10), rep: "", outcome: "", notes: "", advance_status: "none", call_outcome: "none", callback_date: "" };
  const [touchOpen, setTouchOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [touch, setTouch] = useState(emptyTouch);
  const [loggingTouch, setLoggingTouch] = useState(false);

  // create follow-up (next action)
  const emptyFollowUp = { type: "Call back", customType: "", date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10), note: "", owner: "" };
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUp, setFollowUp] = useState(emptyFollowUp);
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  // meeting notes dialog (AI-structured)
  const emptyMeeting = { raw_notes: "", meeting_date: new Date().toISOString().slice(0, 10), advance_status: "demo_completed" };
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState(emptyMeeting);
  const [savingMeeting, setSavingMeeting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agents/mapping`).then((r) => setStaff((r.data.agents || []).filter((a) => a.id !== "emergency"))).catch(() => {});
  }, []);

  const { map: subMap, listFor: subListFor } = useSubStatuses();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, q, c] = await Promise.all([
        axios.get(`${API}/sales/prospects/${recordId}`),
        axios.get(`${API}/sales/prospects/${recordId}/meetings`).catch(() => ({ data: {} })),
        axios.get(`${API}/quotes`, { params: { link_type: "prospect", link_id: recordId } }).catch(() => ({ data: {} })),
        axios.get(`${API}/sales/prospects/${recordId}/contacts`).catch(() => ({ data: {} })),
      ]);
      const prospect = p.data.prospect;
      setRec(prospect);
      setActivities(prospect.activities || []);
      setMeetings(m.data.meetings || []);
      setQuotes(q.data.quotes || []);
      setContacts(c.data.contacts || []);
    } catch (e) {
      toast.error("Failed to load record");
    } finally {
      setLoading(false);
    }
  }, [recordId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setEnqLoading(true);
    axios.get(`${API}/sales/prospects/${recordId}/enquiry-summary`)
      .then((r) => setEnq(r.data))
      .catch(() => setEnq({ available: false }))
      .finally(() => setEnqLoading(false));
  }, [recordId]);

  useEffect(() => {
    const act = searchParams.get("action");
    if (act === "log-touch" || act === "follow-up") {
      if (act === "log-touch") setTouchOpen(true);
      else { setFollowUp({ ...emptyFollowUp, owner: rec?.assigned_rep || "" }); setFollowUpOpen(true); }
      const next = new URLSearchParams(searchParams); next.delete("action");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refreshEnquiry = () => {
    setEnqLoading(true);
    axios.get(`${API}/sales/prospects/${recordId}/enquiry-summary`, { params: { refresh: true } })
      .then((r) => { setEnq(r.data); toast.success("Enquiry re-summarised"); })
      .catch(() => toast.error("Could not summarise enquiry"))
      .finally(() => setEnqLoading(false));
  };

  const logTouch = async () => {
    setLoggingTouch(true);
    try {
      await axios.post(`${API}/sales/activities`, {
        prospect_id: recordId, type: touch.type, activity_date: touch.activity_date,
        rep: touch.rep || null, outcome: touch.outcome || null, notes: touch.notes || "",
        advance_status: touch.advance_status !== "none" ? touch.advance_status : null,
        call_outcome: touch.type === "call" && touch.call_outcome !== "none" ? touch.call_outcome : null,
        callback_date: touch.call_outcome === "callback_requested" ? (touch.callback_date || null) : null,
      });
      toast.success("Touch logged");
      setTouch(emptyTouch); setTouchOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to log touch"); }
    finally { setLoggingTouch(false); }
  };

  const saveMeeting = async () => {
    if (!meetingForm.raw_notes.trim()) { toast.error("Paste the meeting notes first"); return; }
    setSavingMeeting(true);
    try {
      const res = await axios.post(`${API}/sales/prospects/${recordId}/meetings`, {
        raw_notes: meetingForm.raw_notes,
        meeting_date: meetingForm.meeting_date || null,
        advance_status: meetingForm.advance_status !== "none" ? meetingForm.advance_status : null,
      });
      toast.success(res.data?.followup_created ? "Meeting notes saved · follow-up created" : "Meeting notes saved");
      setMeetingForm(emptyMeeting); setMeetingOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the notes"); }
    finally { setSavingMeeting(false); }
  };

  const toggleActionItem = async (mid, index, next) => {
    setMeetings((prev) => prev.map((m) => m.id === mid
      ? { ...m, action_items: (m.action_items || []).map((ai, i) => (i === index ? { ...ai, done: next } : ai)) }
      : m));
    try {
      await axios.patch(`${API}/sales/prospects/${recordId}/meetings/${mid}/action-items/${index}`, { done: next });
    } catch (e) {
      toast.error("Could not update follow-up");
      load();
    }
  };

  /* An action item only matters once someone owns it with a date — promote it
     to a real to-do so it shows up in that person's My Work. */
  const makeTodoFromAction = async (mid, index, ai) => {
    setMakingTodo(`${mid}-${index}`);
    try {
      const r = await axios.post(`${API}/todos/from-action-item`, {
        link_type: "prospect", link_id: recordId, link_name: rec.company_name,
        meeting_id: mid, index, text: ai.text, owner: ai.owner || null,
      });
      const owner = (r.data.todo?.assignee || {}).name || r.data.todo?.owner_name;
      toast.success(r.data.created
        ? `To-do added${owner ? ` for ${owner}` : ""}`
        : "That action item already has a to-do");
      setMeetings((prev) => prev.map((m) => m.id === mid
        ? { ...m, action_items: (m.action_items || []).map((x, i) => (i === index ? { ...x, todo_id: r.data.todo.id } : x)) }
        : m));
      setTodoKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create that to-do");
    } finally { setMakingTodo(null); }
  };


  const openEdit = () => {
    setEditForm({
      company_name: rec.company_name || "", category: rec.category || "", suburb: rec.suburb || "", country: rec.country || "Australia", currency: (rec.currency || "AUD").toUpperCase(),
      source: rec.source || "", assigned_rep: rec.assigned_rep || "", status: rec.status || "not_contacted",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      // Sub-status belongs to a milestone; crossing milestones invalidates it.
      const crossesMilestone = subTypeFor(editForm.status) !== subTypeFor(rec.status);
      await axios.put(`${API}/sales/prospects/${recordId}`, {
        company_name: editForm.company_name || null, category: editForm.category || null,
        suburb: editForm.suburb || null, country: editForm.country || "Australia", currency: editForm.currency || "AUD", source: editForm.source || null,
        assigned_rep: editForm.assigned_rep || null, status: editForm.status,
        ...(crossesMilestone ? { sub_status: "" } : {}),
      });
      toast.success("Prospect updated"); setEditOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSavingEdit(false); }
  };
  const updateSubStatus = async (value) => {
    const next = value === "__none__" ? "" : value;
    try {
      await axios.put(`${API}/sales/prospects/${recordId}`, { sub_status: next });
      setRec((r) => (r ? { ...r, sub_status: next } : r));
      toast.success(next ? "Sub-status updated" : "Sub-status cleared");
    } catch (e) { toast.error("Failed to update sub-status"); }
  };
  const disqualify = async () => {
    try { await axios.put(`${API}/sales/prospects/${recordId}`, { status: "disqualified" }); toast.success("Prospect disqualified"); load(); }
    catch (e) { toast.error("Failed to disqualify"); }
  };
  const doDelete = async () => {
    try { await axios.delete(`${API}/sales/prospects/${recordId}`); toast.success("Prospect deleted"); navigate("/?tab=sales_prospects"); }
    catch (e) { toast.error("Failed to delete"); }
  };
  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };

  const saveFollowUp = async () => {
    const type = (followUp.type === "Other" ? followUp.customType : followUp.type).trim();
    if (!type) { toast.error("Choose or enter a follow-up type"); return; }
    setSavingFollowUp(true);
    try {
      await axios.post(`${API}/sales/prospects/${recordId}/follow-up/done`, {
        next_action: { type, date: followUp.date || null, note: followUp.note.trim() || null, owner: followUp.owner || null },
      });
      toast.success("Follow-up scheduled");
      setFollowUpOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule the follow-up"); }
    finally { setSavingFollowUp(false); }
  };

  const removeActivity = async () => {
    const it = delItem;
    if (!it) return;
    try {
      if (it.origin === "meeting") await axios.delete(`${API}/sales/prospects/${recordId}/meetings/${it.mid}`);
      else if (it.origin === "hubspot") await axios.delete(`${API}/sales/prospects/${recordId}/hubspot-activities/${encodeURIComponent(it.rawId)}`);
      else await axios.delete(`${API}/sales/activities/${it.rawId}`);
      toast.success("Activity removed");
      setDelItem(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove the activity"); }
  };

  const [openThreads, setOpenThreads] = useState({});
  const timeline = useMemo(() => {
    const norm = (s) => (s || "").replace(/^\s*(re|fwd?)\s*:\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();
    const items = [];
    activities.forEach((a, i) => {
      const t = (a.type || "note").toLowerCase();
      const kind = KIND_GLYPH[t] ? t : "note";
      const occurred = a.occurred_at || null;
      const it = {
        id: `act-${a.id || i}`, rawId: a.id, origin: a.source === "hubspot" ? "hubspot" : "activity",
        kind,
        title: a.outcome || KIND_LABEL[t] || "Activity", author: a.rep, body: a.notes || "",
        // When it happened, never when it was imported.
        date: a.activity_date || a.created_at,
        at: occurred || (a.source === "hubspot" ? null : a.created_at) || null,
        ts: occurred || (a.source === "hubspot" ? a.activity_date : a.created_at)
            || a.activity_date || a.created_at || "",
        source: a.source || null,
      };
      if (kind === "email") {
        it.subject = a.subject || (a.outcome || "").replace(/^Emailed:\s*/i, "").trim() || "(no subject)";
        it.bodyHtml = a.body_html || null;
        it.toEmail = a.to_email || null;
        it.fromEmail = a.from_email || null;
        it.direction = a.direction || null;
        it.captured = a.captured_from === "outlook"
          || a.source === "reply_capture" || a.source === "sent_capture";
        it.attachments = a.attachments || [];
        it.threadKey = a.thread_id || norm(it.subject) || "email";
      }
      if (kind === "followup") {
        // A follow-up's real timeline position is its DUE date (the next step),
        // not the day it was logged. Also flag future ones as Upcoming.
        const isoMatch = (a.outcome || "").match(/(\d{4}-\d{2}-\d{2})/);
        const due = a.due_date || (isoMatch ? isoMatch[1] : null);
        if (due) {
          it.dueDate = due;
          it.date = due;
          it.ts = due;   // a follow-up sits at its due date; no meaningful time-of-day
          it.at = null;
          it.upcoming = due > new Date().toISOString().slice(0, 10);
          it.title = (a.outcome || "Follow-up").replace(/\d{4}-\d{2}-\d{2}/g, (m) => formatDate(m));
        }
      }
      items.push(it);
    });
    meetings.forEach((m, i) => items.push({
      id: `mtg-${m.id || i}`, mid: m.id, origin: "meeting", kind: "meeting", title: m.title || "Meeting",
      author: m.attendees || null, body: m.summary || m.raw_notes || "", date: m.meeting_date || m.created_at,
      at: null, ts: m.meeting_date || m.created_at || "",
      actionItems: m.action_items || [],
    }));
    items.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    // collapse identical sync duplicates (emails are kept, then threaded)
    const map = new Map();
    const deduped = [];
    for (const it of items) {
      if (it.kind === "email") { deduped.push(it); continue; }
      const key = `${it.kind}|${(it.title || "").trim()}|${(it.body || "").trim().slice(0, 80)}`;
      if (map.has(key)) map.get(key).copies += 1;
      else { const c = { ...it, copies: 1 }; map.set(key, c); deduped.push(c); }
    }
    // group emails sharing a thread key into a single thread entry
    const threads = new Map();
    const out = [];
    for (const it of deduped) {
      if (it.kind === "email") {
        if (threads.has(it.threadKey)) threads.get(it.threadKey).messages.push(it);
        else { const th = { id: `thread-${it.threadKey}`, kind: "email", isThread: true, threadKey: it.threadKey, messages: [it] }; threads.set(it.threadKey, th); out.push(th); }
      } else out.push(it);
    }
    for (const th of threads.values()) {
      // A conversation reads forwards, so each reply sits under what it answers.
      th.messages.sort((a, b) => (a.ts || "").localeCompare(b.ts || ""));
      const newest = th.messages[th.messages.length - 1];
      th.date = newest.date;
      th.at = newest.at;
      th.ts = newest.ts;
      th.title = th.messages[0].subject;
      th.author = newest.author;
      th.count = th.messages.length;
      th.captured = th.messages.some((m) => m.captured);
      th.hasInbound = th.messages.some((m) => m.direction === "inbound");
    }
    out.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return out;
  }, [activities, meetings]);

  const counts = useMemo(() => {
    const c = { all: timeline.length };
    for (const f of FILTERS) if (f.key !== "all") c[f.key] = timeline.filter((i) => i.kind === f.key).length;
    return c;
  }, [timeline]);
  const filtered = filter === "all" ? timeline : timeline.filter((i) => i.kind === filter);
  const visible = filtered.slice(0, shown);

  const dateForStatus = useCallback((status) => {
    const a = activities.find((x) => x.advance_status === status);
    return a ? (a.activity_date || a.created_at) : null;
  }, [activities]);

  const openQuote = useMemo(() => quotes.find((q) => q.status === "draft" || q.status === "sent"), [quotes]);
  const lastActivity = useMemo(() => timeline.find((i) => i.date && i.date <= new Date().toISOString().slice(0, 10)) || timeline[0], [timeline]);

  if (loading) return <div className="flex items-center gap-2 text-[#8A857A] p-6" data-testid="pv-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading record…</div>;
  if (!rec) return <div className="p-6 text-[#8A857A]">Record not found.</div>;

  const stage = STAGE_STEPS[rec.status];
  const m1Active = stage?.label === "M1 · To demo";
  const m2Active = stage?.label === "M2 · To client";
  const m1Idx = m1Active ? M1.findIndex((x) => x.s === rec.status) : (m2Active || rec.status === "converted" ? M1.length - 1 : -1);
  const m2Idx = m2Active ? M2.findIndex((x) => x.s === rec.status) : -1;

  // needs-attention derived cells
  const nextOverdue = rec.next_action_date ? (daysUntil(rec.next_action_date) < 0 ? -daysUntil(rec.next_action_date) : 0) : 0;
  const openAsks = (enq?.open_asks || []);
  const attnItems = [
    rec.next_action_date && "next step",
    openAsks.length && `${openAsks.length} open ask${openAsks.length > 1 ? "s" : ""}`,
    nextOverdue > 0 && "one overdue step",
  ].filter(Boolean);
  const hasAttention = rec.next_action_date || openQuote || openAsks.length > 0;
  const quoteExpiry = openQuote?.valid_until ? daysUntil(openQuote.valid_until) : null;
  const oq = openQuote?.totals || {};
  const oqBill = oq.monthly?.count ? "monthly" : oq.one_off?.count ? "one_off" : oq.yearly?.count ? "yearly" : null;
  const oqSuffix = { monthly: "per month", yearly: "per year", one_off: "one-off" };

  const facts = enq?.facts || [];
  const capabilities = enq?.capabilities || [];

  return (
    <div className="space-y-3.5" data-testid="prospect-record-view">
      {/* Breadcrumb */}
      <div className="text-[11.5px] flex items-center gap-1.5" data-testid="pv-breadcrumb">
        <button onClick={() => navigate("/?tab=sales")} className="text-[#8A6D00] hover:underline">Sales</button>
        <span className="text-[#B5AFA3]">/</span>
        <button onClick={() => navigate("/?tab=sales_prospects")} className="text-[#8A6D00] hover:underline">Prospects</button>
        <span className="text-[#B5AFA3]">/</span>
        <span className="text-[#1A1A1A] font-semibold truncate max-w-[280px]">{rec.company_name}</span>
      </div>

      {/* Record header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[25px] font-extrabold text-[#1A1A1A] tracking-[-0.018em] truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{rec.company_name}</h1>
            <OverseasBadge currency={rec.currency} country={rec.country} testid="prospect-record-overseas" />
            <SignupBadge prospect={rec} />
            {rec.trial_end_effective && rec.signup_lifecycle === "trial" && (
              <span className="text-[11.5px] font-semibold" style={{ color: "#6B5600" }}
                data-testid="prospect-record-trial-ends"
                title={rec.trial_end_estimated
                  ? `No end date came through with the sign-up, so this assumes a ${14}-day trial from sign-up.`
                  : "The end date on the sign-up."}>
                Trial ends {rec.trial_end_estimated ? "~" : ""}{formatDate(rec.trial_end_effective)}
                {rec.trial_end_estimated ? " (estimated)" : ""}
              </span>
            )}
              <HeaderStatusPill status={rec.status} />
            {subTypeFor(rec.status) && (
              <Select value={rec.sub_status || "__none__"} onValueChange={updateSubStatus}>
                <SelectTrigger data-testid="pv-sub-status" className="h-[26px] w-auto min-w-[150px] max-w-[260px] rounded-full border-[#DAD5CA] bg-[#FBFAF7] px-3 text-[11.5px] font-semibold text-[#59554D] hover:bg-[#F4F2ED]">
                  <SelectValue placeholder="Set sub-status…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No sub-status —</SelectItem>
                  {subListFor(rec.status).map((it) => (
                    <SelectItem key={it.key} value={it.key} data-testid={`pv-sub-status-opt-${it.key}`}>{it.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="text-[13px] text-[#59554D] mt-1 flex items-center gap-1.5 flex-wrap">
            {[rec.category, rec.suburb, rec.source].filter(Boolean).map((x, i) => (
              <span key={i} className="flex items-center gap-1.5">{i > 0 && <span className="text-[#B5AFA3]">·</span>}{x}</span>
            ))}
            {rec.assigned_rep && <><span className="text-[#B5AFA3]">·</span><span>Rep <span className="text-[#1A1A1A] font-semibold">{rec.assigned_rep}</span></span></>}
            <span className="text-[#B5AFA3]">·</span><span className="mono text-[11.5px] text-[#8A857A]">{displayId(rec)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            {rec.status !== "converted" && (
              <Button onClick={() => setConvertOpen(true)} data-testid="pv-convert"
                className="h-[34px] bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[12px] tracking-[0.04em] font-bold" style={{ border: "1px solid #E0B800" }}>
                Convert to client
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/prospects/${recordId}/email`)} data-testid="pv-send-email"
              className="h-[34px] bg-white border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#F4F2ED] text-[13px] font-semibold gap-1.5">
              <Mail className="w-4 h-4 text-[#8A6D00]" /> Send Email
            </Button>
            <Button variant="outline" onClick={() => setTouchOpen(true)} data-testid="pv-log-touch"
              className="h-[34px] bg-white border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#F4F2ED] text-[13px] font-semibold">
              Log a touch
            </Button>
            <Button variant="outline" onClick={() => { setMeetingForm(emptyMeeting); setMeetingOpen(true); }} data-testid="pv-add-meeting"
              className="h-[34px] bg-white border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#F4F2ED] text-[13px] font-semibold gap-1.5">
              <Sparkles className="w-4 h-4 text-[#3B82F6]" /> Add meeting notes
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-[34px] w-[34px] bg-white border-[#C9C3B6] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="pv-menu"><MoreVertical className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[262px] bg-[#FBFAF7] border-[#DAD5CA] rounded-xl p-1.5" style={{ boxShadow: "var(--shadow-pop)" }}>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={openEdit} data-testid="pv-menu-edit"><Pencil className="w-[15px] h-[15px] text-[#6B6660]" /> Edit prospect</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => navigate(`/prospects/${recordId}/email`)} data-testid="pv-menu-email"><Mail className="w-[15px] h-[15px] text-[#8A6D00]" /> Send email</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => setTouchOpen(true)} data-testid="pv-menu-log"><FileText className="w-[15px] h-[15px] text-[#6B6660]" /> Log activity</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => { setFollowUp({ ...emptyFollowUp, owner: rec?.assigned_rep || "" }); setFollowUpOpen(true); }} data-testid="pv-menu-followup"><CalendarClock className="w-[15px] h-[15px] text-[#6B6660]" /> Create follow-up</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => { setMeetingForm(emptyMeeting); setMeetingOpen(true); }} data-testid="pv-menu-meeting"><Sparkles className="w-[15px] h-[15px] text-[#3B82F6]" /> Add meeting notes</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={openEdit} data-testid="pv-menu-assign"><UserCog className="w-[15px] h-[15px] text-[#6B6660]" /> Assign rep</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => setApprovalOpen(true)} data-testid="pv-menu-approval"><ShieldCheck className="w-[15px] h-[15px] text-[#A8320A]" /> Request approval</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => setHubspotOpen(true)} data-testid="pv-menu-hubspot"><RefreshCw className="w-[15px] h-[15px] text-[#FF7A59]" /> Sync with HubSpot</DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={copyLink} data-testid="pv-menu-copy"><LinkIcon className="w-[15px] h-[15px] text-[#6B6660]" /> Copy link</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#E6E2D9]" />
                <DropdownMenuItem className="rounded-lg items-start gap-2 py-2" style={{ color: "#8E1212" }} onClick={disqualify} data-testid="pv-menu-disqualify">
                  <Ban className="w-[15px] h-[15px] mt-0.5" style={{ color: "#8E1212" }} />
                  <span><span className="text-[13px] font-semibold block">Disqualify</span><span className="text-[11.5px] font-normal text-[#59554D]">Removes it from the pipeline. Reversible from the disqualified filter.</span></span>
                </DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg items-start gap-2 py-2" style={{ color: "#8E1212" }} onClick={() => setConfirmDelete(true)} data-testid="pv-menu-delete">
                  <Trash2 className="w-[15px] h-[15px] mt-0.5" style={{ color: "#8E1212" }} />
                  <span><span className="text-[13px] font-semibold block">Delete prospect</span><span className="text-[11.5px] font-normal text-[#59554D]">Removes the record, its activity and its quote. Cannot be undone.</span></span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {rec.status !== "converted" && (
            <p className="text-[11.5px] text-[#59554D] text-right max-w-[320px]">Converting creates a client record, moves the quote to contracts and notifies Customer Success.</p>
          )}
        </div>
      </div>

      {/* Needs attention */}
      <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[#F6F4EF] border-b border-[#DAD5CA]">
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#C23A00" }} />
            <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Needs attention</span>
            <span className="text-[11.5px] text-[#59554D]">{hasAttention ? (attnItems.join(" · ") || "Review the open items below.") : `Nothing outstanding.${lastActivity?.date ? ` Last contact ${daysAgo(lastActivity.date)}.` : ""}`}</span>
          </div>
          {hasAttention && (
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", background: "#E6E2D9", gap: 1 }}>
              {rec.next_action_date && (
                <div className="bg-white p-4" data-testid="pv-attn-next">
                  <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">Next step</div>
                  <div className="text-[13.5px] font-semibold text-[#1A1A1A]">{NEXT_ACTION_LABELS[rec.next_action_type] || rec.next_action_type || "Follow up"}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="mono text-[11.5px]" style={{ color: nextOverdue > 0 ? "#9A3200" : "#1A1A1A" }}>{formatDate(rec.next_action_date)}</span>
                    {nextOverdue > 0 && <span style={{ background: "#FFE9E0", color: "#9A3200", border: "1px solid #F3BFA8", borderRadius: 999, padding: "1px 7px" }} className="text-[10px] font-bold">{nextOverdue} day{nextOverdue > 1 ? "s" : ""} late</span>}
                  </div>
                  {rec.next_action_owner && <div className="text-[11.5px] text-[#59554D] mt-1" data-testid="pv-attn-next-owner">Assigned to {rec.next_action_owner}</div>}
                </div>
              )}
              {openQuote && (
                <div className="bg-white p-4" data-testid="pv-attn-quote">
                  <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">Open quote</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="mono text-[13px] font-bold text-[#1A1A1A]">{money((oq[oqBill] || {}).total, openQuote?.currency)}</span>
                    {oqBill && <span className="text-[12.5px] text-[#59554D]">{oqSuffix[oqBill]}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <HeaderStatusPill status={openQuote.status === "sent" ? "responded" : "not_contacted"} />
                    {quoteExpiry != null && <span className="mono text-[11px] text-[#59554D]">{quoteExpiry >= 0 ? `expires in ${quoteExpiry} day${quoteExpiry !== 1 ? "s" : ""}` : "expired"}</span>}
                  </div>
                </div>
              )}
              {lastActivity?.date && (
                <div className="bg-white p-4" data-testid="pv-attn-last">
                  <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">Last contact</div>
                  <div className="text-[13.5px] font-semibold text-[#1A1A1A]">{daysAgo(lastActivity.date)}</div>
                  <div className="text-[12.5px] text-[#59554D] mt-1">{KIND_LABEL[lastActivity.kind] ? KIND_LABEL[lastActivity.kind][0] + KIND_LABEL[lastActivity.kind].slice(1).toLowerCase() : "Activity"}{lastActivity.author ? ` · ${lastActivity.author}` : ""} · {formatDate(lastActivity.date)}</div>
                </div>
              )}
              {openAsks.length > 0 && (
                <div className="bg-white p-4" data-testid="pv-attn-asks">
                  <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">Open asks from {(rec.contact_name || "the prospect").split(" ")[0]}</div>
                  <div className="space-y-1.5">
                    {openAsks.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-[12.5px] text-[#3A3630]">
                        <span className="rounded-full shrink-0 mt-1.5" style={{ width: 5, height: 5, background: "#E0B800" }} />{a}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column body */}
      <div className="grid gap-3.5 grid-cols-1 lg:grid-cols-[minmax(0,1.62fr)_minmax(0,1fr)]" style={{ alignItems: "start" }}>
        {/* LEFT */}
        <div className="space-y-3.5 min-w-0">
          {/* Inbound enquiry */}
          {(enq?.available || enqLoading) && (
            <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E6E2D9]">
                  <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Inbound enquiry</span>
                  {rec.created_at && <span className="mono text-[11px] text-[#8A857A]">{formatDate(rec.created_at)}</span>}
                  {enq?.summary && <span className="text-[11.5px] text-[#59554D] truncate">{enq.summary}</span>}
                  <button onClick={refreshEnquiry} disabled={enqLoading} title="Re-summarise from the enquiry" className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A6D00] hover:underline shrink-0" data-testid="pv-enq-refresh">
                    {enqLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Re-summarise
                  </button>
                </div>
                {enqLoading && !enq ? (
                  <div className="p-4 space-y-2"><div className="skeleton h-3.5 w-2/3" /><div className="skeleton h-3.5 w-1/2" /></div>
                ) : (
                  <>
                    {facts.length > 0 && (
                      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", background: "#E6E2D9", gap: 1 }}>
                        {facts.map((f, i) => (
                          <div key={i} className="bg-white p-4">
                            <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">{f.label}</div>
                            <div className="text-[13px] text-[#1A1A1A] leading-[1.5]">{f.value}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {capabilities.length > 0 && (
                      <div className="px-4 py-3.5 border-t border-[#E6E2D9]">
                        <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-2">What they want the system to do</div>
                        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 18, rowGap: 6 }}>
                          {capabilities.map((c, i) => (
                            <div key={i} className="flex items-start gap-2 text-[12.5px] text-[#3A3630]"><span className="rounded-full shrink-0 mt-1.5" style={{ width: 5, height: 5, background: "#8A857A" }} />{c}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {enq?.verbatim && (
                      <>
                        <button onClick={() => setEnqOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-2.5 bg-[#F6F4EF] border-t border-[#E6E2D9] hover:bg-[#F4F2ED] transition-colors" data-testid="pv-enq-verbatim-toggle">
                          <ChevronDown className={`w-4 h-4 text-[#8A857A] transition-transform ${enqOpen ? "rotate-180" : ""}`} />
                          <span className="text-[12.5px] font-semibold text-[#1A1A1A]">Read the full enquiry</span>
                          <span className="text-[11.5px] text-[#8A857A]">Verbatim, as received</span>
                        </button>
                        {enqOpen && (
                          <div className="px-4 py-3.5 bg-[#FBFAF7] border-t border-[#E6E2D9]">
                            <div className="mono text-[11px] text-[#8A857A] mb-2">From {rec.contact_name || "—"}{rec.contact_email ? ` · ${rec.contact_email}` : ""}{rec.created_at ? ` · ${formatDate(rec.created_at)}` : ""}</div>
                            <p className="text-[13px] leading-[1.6] text-[#59554D] whitespace-pre-wrap" style={{ textWrap: "pretty" }}>{enq.verbatim}</p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Activity */}
          <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
            <CardContent className="p-0">
              <style>{`.pv-email-body{font-size:13px;line-height:1.55;color:#1A1A1A;word-break:break-word;overflow-wrap:anywhere}.pv-email-body *{max-width:100%}.pv-email-body img{max-width:100%;height:auto}.pv-email-body a{color:#8A6D00}.pv-email-body p{margin:0 0 8px}.pv-email-body table{max-width:100%!important;width:auto!important}.pv-email-body td,.pv-email-body th{word-break:break-word;max-width:100%}`}</style>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E6E2D9] flex-wrap">
                <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Activity</span>
                <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                  {FILTERS.map((f) => {
                    const n = counts[f.key] ?? 0;
                    const zero = f.key !== "all" && n === 0;
                    const active = filter === f.key;
                    return (
                      <button key={f.key} disabled={zero} onClick={() => { setFilter(f.key); setShown(6); }} data-testid={`pv-filter-${f.key}`}
                        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12px] transition-colors ${active ? "bg-[#FFF8D6] border-[#E0B800] text-[#1A1A1A] font-semibold" : zero ? "bg-white border-[#E6E2D9] text-[#B5AFA3] cursor-default" : "bg-white border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}>
                        {f.label}<span className="mono text-[10.5px]">{n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {visible.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px] font-semibold text-[#8A857A]" data-testid="pv-activity-empty">No activity recorded.</div>
              ) : (
                visible.map((it) => {
                  const Glyph = KIND_GLYPH[it.kind] || StickyNote;
                  const isEmail = it.kind === "email";
                  const open = isEmail && !!openThreads[it.id];
                  return (
                    <div key={it.id} className="grid px-4 py-3.5 border-b border-[#F0EDE6]" style={{ gridTemplateColumns: "30px minmax(0,1fr) auto", columnGap: 12 }} data-testid={`pv-activity-${it.id}`}>
                      <div className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center" style={{ background: "#F4F2ED", border: "1px solid #E6E2D9" }}>
                        <Glyph className="w-[15px] h-[15px] text-[#6B6660]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="mono text-[10px] text-[#8A857A]">{KIND_LABEL[it.kind] || "NOTE"}</span>
                          <span className="text-[13px] font-semibold text-[#1A1A1A]">{it.title}</span>
                          {it.upcoming && <span className="mono text-[9px] px-1.5 py-0.5 rounded bg-[#EAF3EC] text-[#0E6B3F] border border-[#BFE0C9] uppercase tracking-wide" data-testid={`pv-upcoming-${it.id}`}>Upcoming</span>}
                          {isEmail && it.count > 1 && <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-[#EEF4FF] text-[#2E5AAC] border border-[#D5E1F6]" data-testid={`pv-thread-count-${it.id}`}>{it.count} messages</span>}
                          {it.source === "hubspot" && <span className="mono text-[9px] px-1.5 py-0.5 rounded bg-[#FFF1E8] text-[#C25A2B] border border-[#F3D6C5]" data-testid="pv-source-hubspot">HUBSPOT</span>}
                          {it.copies > 1 && <span className="mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#F1EEE6", color: "#59554D" }}>{it.copies} synced copies</span>}
                        </div>
                        {it.author && <div className="text-[11.5px] text-[#59554D] mt-0.5">by {it.author}</div>}
                        {isEmail ? (
                          <>
                            <button onClick={() => setOpenThreads((p) => ({ ...p, [it.id]: !p[it.id] }))}
                              className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#8A6D00] hover:underline mt-1" data-testid={`pv-thread-toggle-${it.id}`}>
                              {open ? "Hide messages" : (it.count > 1 ? `View ${it.count} messages` : "View email")}
                              <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                            </button>
                            {open && (
                              <div className="mt-2 space-y-2" data-testid={`pv-thread-body-${it.id}`}>
                                {it.messages.map((m) => (
                                  <div key={m.id} className="rounded-[10px] border p-3"
                                    style={m.direction === "inbound"
                                      ? { borderColor: "#CFE3D6", background: "#F7FBF8" }
                                      : { borderColor: "#EFEBE1", background: "#FBFAF7" }}
                                    data-testid={`pv-email-msg-${m.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2 mb-1.5">
                                      <div className="min-w-0 mono text-[10.5px] text-[#8A857A] leading-relaxed">
                                        <DirectionBadge direction={m.direction} captured={m.captured}
                                          testId={`pv-email-dir-${m.id}`} />
                                        {m.fromEmail && <div className="truncate">From {m.fromEmail}</div>}
                                        {m.toEmail && <div className="truncate">To {m.toEmail}</div>}
                                      </div>
                                      <div className="flex items-start gap-2 shrink-0">
                                        <span className="mono text-[11px] text-[#59554D] whitespace-nowrap">{rowStamp(m)}</span>
                                        <button onClick={() => setDelItem(m)} title="Remove email"
                                          className="text-[#C4BEB2] hover:text-[#8E1212] transition-colors mt-[1px]" data-testid={`pv-email-delete-${m.id}`}>
                                          <Trash2 className="w-[13px] h-[13px]" />
                                        </button>
                                      </div>
                                    </div>
                                    <EmailBody html={m.bodyHtml} text={m.body}
                                      testId={`pv-email-body-${m.id}`} />
                                    <Attachments items={m.attachments}
                                      testId={`pv-email-attachments-${m.id}`} />
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {it.body && <ActivityBody text={it.body} />}
                            {it.kind === "meeting" && it.actionItems?.length > 0 && (
                              <div className="mt-2 rounded-[10px] border border-[#EFEBE1] bg-[#FBFAF7] p-2.5 space-y-1.5" data-testid={`pv-meeting-actions-${it.mid}`}>
                                <div className="mono text-[10px] text-[#8A857A]">FOLLOW-UPS · {it.actionItems.filter((a) => a.done).length}/{it.actionItems.length} done</div>
                                {it.actionItems.map((ai, idx) => (
                                  <div key={idx} className="flex items-start gap-2 group">
                                    <span onClick={() => toggleActionItem(it.mid, idx, !ai.done)} data-testid={`pv-meeting-action-toggle-${it.mid}-${idx}`}
                                      className={`mt-0.5 w-[16px] h-[16px] rounded-[5px] border flex items-center justify-center shrink-0 cursor-pointer transition-colors ${ai.done ? "bg-[#0E6B3F] border-[#0E6B3F]" : "bg-white border-[#C9C3B6] group-hover:border-[#8A857A]"}`}>
                                      {ai.done && <Check className="w-[11px] h-[11px] text-white" />}
                                    </span>
                                    <span className={`text-[13px] leading-[1.5] flex-1 ${ai.done ? "line-through text-[#8A857A]" : "text-[#1A1A1A]"}`}>
                                      {ai.text}{ai.owner ? <span className="text-[#8A857A]"> · {ai.owner}</span> : null}
                                    </span>
                                    {ai.todo_id ? (
                                      <span className="mono text-[10px] text-[#0E6B3F] shrink-0 mt-0.5" data-testid={`pv-action-todo-made-${it.mid}-${idx}`}>TO-DO ADDED</span>
                                    ) : (
                                      <button onClick={() => makeTodoFromAction(it.mid, idx, ai)} disabled={makingTodo === `${it.mid}-${idx}`}
                                        className="shrink-0 text-[11px] font-semibold text-[#8A6D00] hover:underline disabled:opacity-50"
                                        data-testid={`pv-action-make-todo-${it.mid}-${idx}`}>
                                        {makingTodo === `${it.mid}-${idx}` ? "Adding…" : "Make to-do"}
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {it.copies > 1 && <button onClick={() => toast.info("Duplicate review from HubSpot is coming soon.")} className="text-[12px] font-semibold text-[#8A6D00] hover:underline mt-1" data-testid="pv-review-dup">Review duplicate from HubSpot</button>}
                          </>
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="mono text-[11.5px] text-[#59554D] whitespace-nowrap">{rowStamp(it)}</span>
                        {!isEmail && (
                          <button onClick={() => setDelItem(it)} title="Remove activity"
                            className="text-[#C4BEB2] hover:text-[#8E1212] transition-colors shrink-0 mt-[1px]" data-testid={`pv-activity-delete-${it.id}`}>
                            <Trash2 className="w-[14px] h-[14px]" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              {filtered.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-[#FBFAF7] border-t border-[#E6E2D9]">
                  <span className="mono text-[11.5px] text-[#59554D]">{Math.min(shown, filtered.length)} of {filtered.length} activity records</span>
                  {shown < filtered.length && <Button variant="outline" size="sm" onClick={() => setShown((s) => s + 10)} className="h-7 bg-white border-[#C9C3B6] text-[#59554D] hover:bg-[#F4F2ED] text-[12px]" data-testid="pv-load-more">Load earlier activity</Button>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT RAIL */}
        <div className="space-y-3.5 min-w-0">
          <ContactsEditor base={`${API}/sales/prospects/${recordId}`} />

          <EntityTodosCard linkType="prospect" linkId={recordId} linkName={rec.company_name}
            refreshKey={todoKey} testid="prospect-todos" />

          <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E6E2D9]">
                <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Quotes</span>
                {quotes.length > 0 && <span className="mono text-[11px] text-[#59554D]">{quotes.length}</span>}
              </div>
              <div className="p-3">
                <QuotesPanel linkType="prospect" linkId={recordId} linkName={rec.company_name} contacts={contacts} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E6E2D9]">
                <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Stage progress</span>
                {stage && <span className="mono text-[11px] text-[#59554D] ml-auto">{stage.step}/{stage.total}</span>}
              </div>
              <div className="p-4 space-y-4">
                <StageTrack title="M1 · To demo" steps={M1} currentIndex={m1Idx} activeTrack={m1Active} dateFor={dateForStatus} />
                <StageTrack title="M2 · To client" steps={M2} currentIndex={m2Idx} activeTrack={m2Active} dateFor={dateForStatus} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E6E2D9]">
                <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Record</span>
              </div>
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", background: "#E6E2D9", gap: 1 }}>
                {[
                  ["Rep", rec.assigned_rep || "—", false],
                  ["Category", rec.category || "—", false],
                  ["Region", rec.suburb || "—", false],
                  ["Source", rec.source || "—", false],
                  ["Created", rec.created_at ? formatDate(rec.created_at) : "—", true],
                  ["Last synced", rec.updated_at ? formatDate(rec.updated_at) : "—", true],
                ].map(([k, v, mono]) => (
                  <div key={k} className="bg-white p-3">
                    <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">{k}</div>
                    <div className={`text-[12.5px] text-[#1A1A1A] ${mono ? "mono" : ""}`}>{v}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Convert dialog */}
      <ConvertToClientDialog prospect={rec} open={convertOpen} staff={staff} onOpenChange={setConvertOpen}
        onDone={(res) => { if (res?.client_id) navigate(`/clients/${encodeURIComponent(res.client_id)}`); }} />


      {/* Delete confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>        <DialogContent className="bg-white border-[#DAD5CA] max-w-md" data-testid="pv-delete-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-[#C23A00]" /> Delete this prospect?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#59554D]">Deleting <span className="font-semibold text-[#1A1A1A]">{rec.company_name}</span> removes the record, its activity and its quote. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={doDelete} className="bg-[#C23A00] hover:bg-[#A83100] text-white" data-testid="pv-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-[#DAD5CA] max-w-lg" data-testid="pv-edit-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Edit prospect</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#59554D]">Company</label>
                <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="pv-edit-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#59554D]">Category</label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid="pv-edit-category" /></div>
                <div><label className="text-xs text-[#59554D]">Suburb / region</label><Input value={editForm.suburb} onChange={(e) => setEditForm({ ...editForm, suburb: e.target.value })} data-testid="pv-edit-suburb" /></div>
                <div><label className="text-xs text-[#59554D]">Country</label><Input value={editForm.country ?? "Australia"} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} data-testid="pv-edit-country" /></div>
                <div><label className="text-xs text-[#59554D]">Quote currency</label>
                  <select value={(editForm.currency || "AUD").toUpperCase()} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })} data-testid="pv-edit-currency"
                    className="w-full h-10 rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px]">
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div><label className="text-xs text-[#59554D]">Source</label><Input value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })} data-testid="pv-edit-source" /></div>
                <div>
                  <label className="text-xs text-[#59554D]">Rep</label>
                  <Select value={editForm.assigned_rep || "unassigned"} onValueChange={(v) => setEditForm({ ...editForm, assigned_rep: v === "unassigned" ? "" : v })}>
                    <SelectTrigger data-testid="pv-edit-rep"><SelectValue placeholder="Rep" /></SelectTrigger>
                    <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{staff.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[#59554D]">Stage</label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="pv-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={savingEdit} className="text-[#59554D]">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="pv-edit-save">{savingEdit && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log a touch dialog */}
      <Dialog open={touchOpen} onOpenChange={setTouchOpen}>
        <DialogContent className="max-w-lg" data-testid="pv-touch-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Log a touch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Type</label>
                <Select value={touch.type} onValueChange={(v) => setTouch({ ...touch, type: v })}>
                  <SelectTrigger data-testid="pv-touch-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Date</label>
                <Input type="date" value={touch.activity_date} onChange={(e) => setTouch({ ...touch, activity_date: e.target.value })} data-testid="pv-touch-date" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Rep</label>
                <Select value={touch.rep || "unassigned"} onValueChange={(v) => setTouch({ ...touch, rep: v === "unassigned" ? "" : v })}>
                  <SelectTrigger data-testid="pv-touch-rep"><SelectValue placeholder="Rep" /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">—</SelectItem>{staff.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Advance status to</label>
                <Select value={touch.advance_status} onValueChange={(v) => setTouch({ ...touch, advance_status: v })}>
                  <SelectTrigger data-testid="pv-touch-advance"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Leave unchanged</SelectItem>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {touch.type === "call" && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-[#DAD5CA] bg-[#F4F2ED] p-3">
                <div>
                  <label className="text-xs text-[#59554D]">Call outcome (auto-schedules next action)</label>
                  <Select value={touch.call_outcome} onValueChange={(v) => setTouch({ ...touch, call_outcome: v })}>
                    <SelectTrigger data-testid="pv-call-outcome"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— No auto-action —</SelectItem>{CALL_OUTCOME_ORDER.map((c) => <SelectItem key={c} value={c}>{CALL_OUTCOME_LABELS[c]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {touch.call_outcome === "callback_requested" && (
                  <div><label className="text-xs text-[#59554D]">Callback date</label><Input type="date" value={touch.callback_date} onChange={(e) => setTouch({ ...touch, callback_date: e.target.value })} data-testid="pv-callback-date" /></div>
                )}
              </div>
            )}
            <Input value={touch.outcome} onChange={(e) => setTouch({ ...touch, outcome: e.target.value })} placeholder="Outcome (e.g. left voicemail, positive reply)" data-testid="pv-touch-outcome" />
            <Textarea value={touch.notes} onChange={(e) => setTouch({ ...touch, notes: e.target.value })} placeholder="Notes" className="min-h-[70px]" data-testid="pv-touch-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTouchOpen(false)} disabled={loggingTouch} data-testid="pv-touch-cancel">Cancel</Button>
            <Button className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" onClick={logTouch} disabled={loggingTouch} data-testid="pv-touch-save">{loggingTouch && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Log touch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add meeting notes dialog (AI-structured) */}
      <Dialog open={meetingOpen} onOpenChange={(o) => { if (!o && !savingMeeting) setMeetingOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pv-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
              <Sparkles className="w-4 h-4 text-[#3B82F6]" /> Add meeting notes{rec ? ` — ${rec.company_name}` : ""}
            </DialogTitle>
            <p className="text-xs text-[#59554D]">Paste the raw Teams AI meeting notes. We'll auto-summarise them, log a meeting, and turn your action items into a follow-up.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Meeting date</label>
                <Input type="date" value={meetingForm.meeting_date}
                  onChange={(e) => setMeetingForm({ ...meetingForm, meeting_date: e.target.value })}
                  data-testid="pv-meeting-date" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Set status to</label>
                <Select value={meetingForm.advance_status} onValueChange={(v) => setMeetingForm({ ...meetingForm, advance_status: v })}>
                  <SelectTrigger data-testid="pv-meeting-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Leave unchanged</SelectItem>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Meeting notes (paste from Teams)</label>
              <Textarea value={meetingForm.raw_notes} onChange={(e) => setMeetingForm({ ...meetingForm, raw_notes: e.target.value })}
                placeholder="Paste the full AI meeting notes here — purpose, takeaways, summary, action items…"
                className="min-h-[240px] text-xs" data-testid="pv-meeting-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMeetingOpen(false)} disabled={savingMeeting} className="text-[#59554D]" data-testid="pv-meeting-cancel">Cancel</Button>
            <Button onClick={saveMeeting} disabled={savingMeeting || !meetingForm.raw_notes.trim()}
              className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="pv-meeting-save">
              {savingMeeting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {savingMeeting ? "Summarising…" : "Summarise & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create follow-up dialog */}
      <Dialog open={followUpOpen} onOpenChange={(o) => { if (!o && !savingFollowUp) setFollowUpOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="pv-followup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
              <CalendarClock className="w-4 h-4 text-[#6B6660]" /> Create follow-up{rec ? ` — ${rec.company_name}` : ""}
            </DialogTitle>
            <p className="text-xs text-[#59554D]">Schedule the next action for this prospect. It shows in “Needs attention” and the pipeline reminders.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Type</label>
                <Select value={followUp.type} onValueChange={(v) => setFollowUp({ ...followUp, type: v })}>
                  <SelectTrigger data-testid="pv-followup-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Call back", "Send email", "Send quote", "Demo follow-up", "Check in", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Due date</label>
                <Input type="date" value={followUp.date} onChange={(e) => setFollowUp({ ...followUp, date: e.target.value })} data-testid="pv-followup-date" />
              </div>
            </div>
            {followUp.type === "Other" && (
              <div>
                <label className="text-xs text-[#59554D]">Custom type</label>
                <Input value={followUp.customType} onChange={(e) => setFollowUp({ ...followUp, customType: e.target.value })} placeholder="e.g. Send onboarding pack" data-testid="pv-followup-customtype" />
              </div>
            )}
            <div>
              <label className="text-xs text-[#59554D]">Assign to</label>
              <Select value={followUp.owner || "unassigned"} onValueChange={(v) => setFollowUp({ ...followUp, owner: v === "unassigned" ? "" : v })}>
                <SelectTrigger data-testid="pv-followup-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {staff.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Note (optional)</label>
              <Textarea value={followUp.note} onChange={(e) => setFollowUp({ ...followUp, note: e.target.value })} placeholder="What needs doing on this follow-up?" className="min-h-[80px] text-xs" data-testid="pv-followup-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFollowUpOpen(false)} disabled={savingFollowUp} className="text-[#59554D]" data-testid="pv-followup-cancel">Cancel</Button>
            <Button onClick={saveFollowUp} disabled={savingFollowUp} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="pv-followup-save">
              {savingFollowUp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarClock className="w-4 h-4 mr-1" />}
              {savingFollowUp ? "Scheduling…" : "Schedule follow-up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove activity confirm */}
      <Dialog open={!!delItem} onOpenChange={(o) => { if (!o) setDelItem(null); }}>
        <DialogContent className="max-w-md" data-testid="pv-delete-activity-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Remove activity?</DialogTitle></DialogHeader>
          <p className="text-[13px] text-[#59554D] leading-[1.6]">
            This removes “{delItem?.title || "this entry"}” from the timeline. This can’t be undone.
            {delItem?.origin === "hubspot" ? " (It's synced from HubSpot and may return on the next sync.)" : ""}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelItem(null)} className="text-[#59554D]" data-testid="pv-delete-activity-cancel">Cancel</Button>
            <Button onClick={removeActivity} className="text-white" style={{ background: "#8E1212" }} data-testid="pv-delete-activity-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {hubspotOpen && rec && (
        <ProspectHubspotSyncDialog prospect={rec} onClose={() => setHubspotOpen(false)}
          onSynced={load} />
      )}
      <RequestApprovalDialog open={approvalOpen} onOpenChange={setApprovalOpen} record={{
        suggested_title: `Approve something on ${rec?.company_name || "this prospect"}`,
        link_type: "prospect", link_id: recordId, link_name: rec?.company_name,
        link_path: `/prospects/${recordId}`,
      }} />
    </div>
  );
}
